/**
 * Schedule & Shift Management API Client
 */
import { config } from '../config';
const SCHEDULE_API_URL = config.api.schedule;
/**
 * Helper to build authenticated request headers
 */
function getHeaders(token, tenantId) {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
    };
}
/**
 * Helper to handle API responses
 */
async function handleResponse(response) {
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }
    return response.json();
}
// ==================== Schedule Management ====================
/**
 * Create a new schedule
 */
export async function createSchedule(token, tenantId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * List all schedules with optional filtering
 */
export async function listSchedules(token, tenantId, params) {
    const queryParams = new URLSearchParams();
    if (params?.status)
        queryParams.set('status', params.status);
    if (params?.limit)
        queryParams.set('limit', params.limit.toString());
    if (params?.nextToken)
        queryParams.set('nextToken', params.nextToken);
    const url = `${SCHEDULE_API_URL}/schedules${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
/**
 * Get a specific schedule by ID
 */
export async function getSchedule(token, tenantId, scheduleId) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}`, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
/**
 * Update a schedule
 */
export async function updateSchedule(token, tenantId, scheduleId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}`, {
        method: 'PUT',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * Publish a schedule (manager/admin only)
 */
export async function publishSchedule(token, tenantId, scheduleId) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/publish`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
// ==================== Shift Management ====================
/**
 * Create a new shift
 */
export async function createShift(token, tenantId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/shifts`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * List shifts for a schedule
 */
export async function listShifts(token, tenantId, scheduleId, startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate)
        params.append('startDate', startDate);
    if (endDate)
        params.append('endDate', endDate);
    const url = `${SCHEDULE_API_URL}/schedules/${scheduleId}/shifts${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
/**
 * Get a specific shift by ID
 */
export async function getShift(token, tenantId, shiftId) {
    const response = await fetch(`${SCHEDULE_API_URL}/shifts/${shiftId}`, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
/**
 * Update a shift
 */
export async function updateShift(token, tenantId, shiftId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/shifts/${shiftId}`, {
        method: 'PUT',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * Delete a shift
 */
export async function deleteShift(token, tenantId, shiftId) {
    const response = await fetch(`${SCHEDULE_API_URL}/shifts/${shiftId}`, {
        method: 'DELETE',
        headers: getHeaders(token, tenantId),
    });
    if (!response.ok) {
        throw new Error(`Failed to delete shift: ${response.status}`);
    }
}
/**
 * Assign an employee to a shift
 */
export async function assignShift(token, tenantId, shiftId, employeeId) {
    const response = await fetch(`${SCHEDULE_API_URL}/shifts/${shiftId}/assign`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify({ employeeId }),
    });
    return handleResponse(response);
}
// ==================== Employee Views ====================
/**
 * Get my assigned shifts (employee view)
 */
export async function getMySchedule(token, tenantId, params) {
    const queryParams = new URLSearchParams();
    if (params?.startDate)
        queryParams.set('startDate', params.startDate);
    if (params?.endDate)
        queryParams.set('endDate', params.endDate);
    const url = `${SCHEDULE_API_URL}/my-schedule${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
/**
 * Get team schedule (manager view)
 */
export async function getTeamSchedule(token, tenantId, params) {
    const queryParams = new URLSearchParams();
    if (params?.startDate)
        queryParams.set('startDate', params.startDate);
    if (params?.endDate)
        queryParams.set('endDate', params.endDate);
    const url = `${SCHEDULE_API_URL}/team-schedule${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
// ==================== Shift Templates ====================
/**
 * Create a shift template
 */
export async function createTemplate(token, tenantId, scheduleId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/templates`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * List all shift templates for a schedule
 */
export async function listTemplates(token, tenantId, scheduleId) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/templates`, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
/**
 * Batch create shift templates
 */
export async function batchCreateTemplates(token, tenantId, scheduleId, templates) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/templates/batch`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify({ templates }),
    });
    return handleResponse(response);
}
// ==================== Shift Swaps ====================
/**
 * Request a shift swap
 */
export async function requestShiftSwap(token, tenantId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/swaps`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * Review a shift swap (manager/admin only)
 */
export async function reviewShiftSwap(token, tenantId, swapId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/swaps/${swapId}/review`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * List shift swaps
 */
export async function listShiftSwaps(token, tenantId, params) {
    const queryParams = new URLSearchParams();
    if (params?.status)
        queryParams.set('status', params.status);
    const url = `${SCHEDULE_API_URL}/swaps${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
// ==================== Shift Exceptions ====================
/**
 * Create a shift exception (cancel, modify, or add a shift for a specific date)
 */
export async function createException(token, tenantId, scheduleId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/exceptions`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
/**
 * List exceptions for a schedule within a date range
 */
export async function listExceptions(token, tenantId, scheduleId, params) {
    const queryParams = new URLSearchParams();
    if (params?.startDate)
        queryParams.set('startDate', params.startDate);
    if (params?.endDate)
        queryParams.set('endDate', params.endDate);
    const url = `${SCHEDULE_API_URL}/schedules/${scheduleId}/exceptions${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url, {
        headers: getHeaders(token, tenantId),
    });
    return handleResponse(response);
}
/**
 * Delete a shift exception
 */
export async function deleteException(token, tenantId, scheduleId, exceptionId) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/exceptions/${exceptionId}`, {
        method: 'DELETE',
        headers: getHeaders(token, tenantId),
    });
    if (!response.ok) {
        throw new Error(`Failed to delete exception: ${response.status}`);
    }
}
// =============================================================================
// Schedule Assignment API
// =============================================================================
/**
 * Create Assignment - Assign schedule to employee
 */
export async function createAssignment(token, tenantId, scheduleId, data) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/assignments`, {
        method: 'POST',
        headers: getHeaders(token, tenantId),
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw new Error(`Failed to create assignment: ${response.status}`);
    }
    return response.json();
}
/**
 * List Assignments - Get all assignments for a schedule
 */
export async function listAssignments(token, tenantId, scheduleId, status) {
    const params = new URLSearchParams();
    if (status)
        params.append('status', status);
    const url = `${SCHEDULE_API_URL}/schedules/${scheduleId}/assignments${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token, tenantId),
    });
    if (!response.ok) {
        throw new Error(`Failed to list assignments: ${response.status}`);
    }
    return response.json();
}
/**
 * Get My Schedules - Employee view of assigned schedules
 */
export async function getMySchedules(token, tenantId, status) {
    const params = new URLSearchParams();
    if (status)
        params.append('status', status);
    const url = `${SCHEDULE_API_URL}/my-schedules${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token, tenantId),
    });
    if (!response.ok) {
        throw new Error(`Failed to get my schedules: ${response.status}`);
    }
    return response.json();
}
/**
 * Delete Assignment - Unassign employee from schedule
 */
export async function deleteAssignment(token, tenantId, scheduleId, employeeId) {
    const response = await fetch(`${SCHEDULE_API_URL}/schedules/${scheduleId}/assignments/${employeeId}`, {
        method: 'DELETE',
        headers: getHeaders(token, tenantId),
    });
    if (!response.ok) {
        throw new Error(`Failed to delete assignment: ${response.status}`);
    }
}
